import { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionPreviewState } from '../utils';
import SessionPreviewHeader from './SessionPreviewHeader';
import SessionPreviewFooter from './SessionPreviewFooter';
import type { SessionPreviewProps } from './types';
import { classnames } from '@/libs/utils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

function SessionPreview({ session }: SessionPreviewProps) {
  const navigate = useNavigate();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isCLHomePageEnabled = Boolean(featureFlags[FeatureFlagKeys.Use_CL_New_HomePage]);

  const {
    projectId,
    sessionId,
    isContentUploadInProgress,
    contentStatusText,
    canPreviewSession,
    canOpenSession,
    isRecordingEventProcessing,
    isVideoReadyForPreview,
    isDisabled,
    isBatchFailed,
    failedIconName,
    endTime,
    newUploadBadge
  } = useMemo(() => getSessionPreviewState(session), [session]);

  const handleContentClick = useCallback(() => {
    if (isRecordingEventProcessing) {
      window.location.replace(`/recording-event/${projectId}`);
      return;
    }

    // Simplified-upload cards are openable only once upload_editor_status is DONE (see
    // getSimplifiedUploadPreviewState); DONE is terminal, so we can route straight into the editor
    // without a live status re-check. Non-openable cards early-return here.
    if (!canOpenSession) {
      return;
    }

    navigate(isContentUploadInProgress ? `/import/${projectId}/${sessionId}` : `/${projectId}/${sessionId}/recordings`);
  }, [canOpenSession, isContentUploadInProgress, isRecordingEventProcessing, navigate, projectId, sessionId]);

  return (
    <button
      className={classnames(
        'flex w-[23rem] shrink-0 flex-col divide-y divide-slate-200/80 truncate !rounded-xl border !border-slate-200/80',
        {
          'cursor-pointer transition-all duration-[250ms] ease-in-out hover:scale-[1.0075] hover:shadow-lg hover:shadow-slate-200':
            isVideoReadyForPreview,
          'cursor-not-allowed': isDisabled,
          'h-[235px] w-[298px] hover:!shadow-md': isCLHomePageEnabled
        }
      )}
      onClick={handleContentClick}
      data-testid="session-preview"
    >
      <SessionPreviewHeader
        session={session}
        canPreviewSession={canPreviewSession}
        isBatchFailed={isBatchFailed}
        failedIconName={failedIconName}
        contentStatusText={contentStatusText}
      />
      <SessionPreviewFooter
        title={session.title}
        endTime={endTime}
        isCLHomePageEnabled={isCLHomePageEnabled}
        isVideoReadyForPreview={isVideoReadyForPreview}
        contentStatusText={contentStatusText}
        newUploadBadge={newUploadBadge}
      />
    </button>
  );
}

export default memo(SessionPreview);
